import { useMemo, useState } from 'react';
import { Kaart, KaartKop, Knop, Leeg, PaginaKop, Schakelaar, SkeletTabel, SpelerCel } from '../components/ui';
import { START_RATING, huidigSeizoen, potjesVan, seizoenLabel, seizoensRatings } from '../lib/elo';
import { toonRating } from '../lib/format';
import { aantalPotjesVoor, genereerPotjes } from '../lib/prestaties';
import { usePoule } from '../lib/poule';
import { useLeague } from '../lib/queries';

type Bereik = 'altijd' | 'seizoen';

/*
 * Vink aan wie er meedoen en de app maakt er hele potjes van. Zeven man wordt
 * twee potjes: acht plekken, dus iemand speelt twee keer. Wie dat is wordt niet
 * geloot maar gekozen op aantal gespeelde potjes, zodat het over een middag
 * vanzelf glad loopt.
 */
export default function Indelen() {
  const { pouleId } = usePoule();
  const { players, matches, rating, zoekSpeler, laden } = useLeague(pouleId);
  const [gekozen, setGekozen] = useState<string[]>([]);
  const [uitkomst, setUitkomst] = useState<ReturnType<typeof genereerPotjes> | null>(null);
  const [loten, setLoten] = useState(false);
  const [bereik, setBereik] = useState<Bereik>('altijd');

  /*
   * Twee maatstaven voor dezelfde tafel. De rating van altijd kent iedereen
   * door en door; de seizoensrating begint elk kwartaal met een zachte reset en
   * weegt dus zwaarder wat er de laatste maanden gebeurde. Wie net in vorm is
   * komt daar hoger uit, en de teams vallen navenant anders.
   */
  const seizoen = huidigSeizoen();
  const seizoenRatings = useMemo(() => seizoensRatings(seizoen, matches), [seizoen, matches]);
  const sterkte = (id: string) =>
    bereik === 'seizoen' ? seizoenRatings[id] ?? START_RATING : rating(id);

  if (laden) {
    return (
      <>
        <PaginaKop titel="Indelen" />
        <Kaart>
          <SkeletTabel rijen={3} kolommen={3} />
        </Kaart>
      </>
    );
  }

  const actief = players.filter((p) => p.isActive);
  const wissel = (id: string) => {
    setUitkomst(null);
    setGekozen((oud) => (oud.includes(id) ? oud.filter((x) => x !== id) : [...oud, id]));
  };
  const kiesBereik = (b: Bereik) => {
    setBereik(b);
    /* De vorige indeling is op de andere maatstaf gemaakt; die laten staan zou
       een verkeerde indruk wekken. */
    setUitkomst(null);
  };

  const voorspeld = aantalPotjesVoor(gekozen.length);
  const gespeeld = (id: string) => potjesVan(id, matches).length;

  return (
    <>
      <PaginaKop
        titel="Indelen"
        onder={
          loten
            ? 'Kies wie er meedoen; de teams worden zuiver geloot, zonder naar de rating te kijken.'
            : `Kies wie er meedoen; de app verdeelt ze over hele potjes met zo gelijk mogelijke teams, gewogen op de rating ${
                bereik === 'seizoen' ? `van ${seizoenLabel(seizoen)}` : 'van altijd'
              }.`
        }
        rechts={
          <>
            <Schakelaar<Bereik>
              waarde={bereik}
              onKies={kiesBereik}
              opties={[
                { waarde: 'altijd', label: 'Aller tijden' },
                { waarde: 'seizoen', label: 'Dit seizoen' }
              ]}
            />
            <Knop
              variant="primair"
              disabled={gekozen.length < 4}
              onClick={() => setUitkomst(genereerPotjes(gekozen, sterkte, gespeeld, undefined, loten))}
            >
              Maak {voorspeld ? voorspeld : ''} {voorspeld === 1 ? 'potje' : 'potjes'}
            </Knop>
          </>
        }
      />

      <Kaart>
        <KaartKop
          titel="Wie doen er mee"
          hint={
            gekozen.length
              ? `${gekozen.length} geselecteerd${
                  gekozen.length >= 4
                    ? ` · goed voor ${voorspeld} ${voorspeld === 1 ? 'potje' : 'potjes'}`
                    : ' · minstens vier nodig'
                }`
              : undefined
          }
        />
        <div className="flex flex-wrap gap-2">
          {actief.map((p) => {
            const aan = gekozen.includes(p.id);
            return (
              <button
                key={p.id}
                onClick={() => wissel(p.id)}
                className={`flex items-center gap-2 rounded-sm border px-2.5 py-1.5 text-sm transition ${
                  aan
                    ? 'border-veld-700 bg-veld-50 font-semibold text-veld-900'
                    : 'border-krijt-300 hover:bg-krijt-100'
                }`}
              >
                <SpelerCel speler={p} link={false} />
                <span className="nums text-xs text-krijt-500">{toonRating(sterkte(p.id))}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Knop
            onClick={() => {
              setGekozen(actief.map((p) => p.id));
              setUitkomst(null);
            }}
          >
            Iedereen
          </Knop>
          {gekozen.length ? (
            <Knop
              onClick={() => {
                setGekozen([]);
                setUitkomst(null);
              }}
            >
              Selectie wissen
            </Knop>
          ) : null}

          <label className="ml-auto flex cursor-pointer items-center gap-2 text-sm text-krijt-600">
            <input
              type="checkbox"
              checked={loten}
              onChange={(e) => {
                setLoten(e.target.checked);
                setUitkomst(null);
              }}
            />
            Zuiver loten, niet op sterkte
          </label>
        </div>
      </Kaart>

      {uitkomst ? (
        <div className="mt-3.5">
          <Kaart>
            <KaartKop titel="De indeling" hint={uitkomst.uitleg} />
            {uitkomst.potjes.length ? (
              <div className="flex flex-col gap-2.5">
                {uitkomst.potjes.map((p, i) => (
                  <div key={i} className="border border-krijt-200 px-3.5 py-3">
                    <div className="opschrift mb-2 text-krijt-500">Potje {i + 1}</div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                      <span className="flex items-center gap-2">
                        <SpelerCel speler={zoekSpeler(p.teamA[0])} />
                        <span className="text-krijt-400">+</span>
                        <SpelerCel speler={zoekSpeler(p.teamA[1])} />
                      </span>
                      <span className="kop text-[15px] text-krijt-400">vs</span>
                      <span className="flex items-center gap-2">
                        <SpelerCel speler={zoekSpeler(p.teamB[0])} />
                        <span className="text-krijt-400">+</span>
                        <SpelerCel speler={zoekSpeler(p.teamB[1])} />
                      </span>
                      <span className="ml-auto text-xs text-krijt-500">
                        <span className="nums">{Math.round(p.verschil)}</span> punten verschil ·{' '}
                        <span className="nums">{Math.round(p.kansA)}</span>–
                        <span className="nums">{100 - Math.round(p.kansA)}</span>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Leeg tekst="Met dit aantal spelers valt er geen heel potje te maken." ikoon="duo" />
            )}

            {uitkomst.dubbel.length || uitkomst.bank.length ? (
              <div className="mt-3 flex flex-col gap-1.5 border-t border-krijt-200 pt-3 text-sm text-krijt-600">
                {uitkomst.dubbel.length ? (
                  <p>
                    <strong>Speelt twee keer:</strong>{' '}
                    {uitkomst.dubbel.map((id) => zoekSpeler(id).name).join(', ')} — wie de minste potjes op de
                    teller heeft.
                  </p>
                ) : null}
                {uitkomst.bank.length ? (
                  <p>
                    <strong>Kijkt deze ronde toe:</strong>{' '}
                    {uitkomst.bank.map((id) => zoekSpeler(id).name).join(', ')} — wie er al het meest heeft
                    gespeeld.
                  </p>
                ) : null}
              </div>
            ) : null}

            <p className="mt-3 text-xs text-krijt-500">
              De eerste naam van elk duo staat achterin, de tweede voorin. Voer de uitslagen daarna in bij
              Uitslag; deze indeling wordt zelf niet opgeslagen.
              {loten
                ? ' Geloot betekent geloot: de rating is buiten beschouwing gelaten, dus de teams kunnen scheef uitvallen.'
                : ` Gewogen op de rating ${
                    bereik === 'seizoen' ? `van ${seizoenLabel(seizoen)}` : 'van alle tijden'
                  }.`}
            </p>
          </Kaart>
        </div>
      ) : null}
    </>
  );
}
